import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  ScrollView,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Text } from '@/components/ui/text';
import { Button } from '@/components/ui/button';
import { apiRequest } from '../../services/apiClient';
import { ADMIN_COLORS } from '@/components/admin/AdminUI';
import {
  assignmentSavePayload,
  assignmentTransport,
  isClaudeSlug,
  rollbackToOpenRouterPayload,
  type ReasoningEffort,
  type RoutedAssignment,
} from '../../utils/claudeGateway';

interface ModelCatalogEntry {
  slug: string;
  display_name: string;
  provider: string;
  flags: string[];
  notes: string;
  enabled: boolean;
  transport?: string;
}

interface ModelCatalogResponse {
  models: ModelCatalogEntry[];
  version: string;
}

type WorkloadAssignment = RoutedAssignment;

interface WorkloadAssignmentsResponse {
  assignments: Record<string, WorkloadAssignment>;
  workload_keys: string[];
  version: string;
}

const REASONING_OPTIONS: ReasoningEffort[] = [null, 'minimal', 'low', 'medium', 'high', 'xhigh', 'max'];

const WORKLOAD_LABELS: Record<string, string> = {
  'analysis.phase1_extract': 'Analysis Phase 1: Extract',
  'analysis.phase2_evaluate': 'Analysis Phase 2: Judgment',
  'analysis.phase2_adjudicate': 'Analysis Phase 2: Adjudicator',
  'analysis.msr_marker_audit': 'Analysis: MSR Marker Audit',
  'analysis.phase3_narrative': 'Analysis Phase 3: Narrative',
  'analysis.custom_prompt': 'Analysis: Custom Prompt',
  'mentor_forms.crf_narrative': 'Mentor Forms: CRF Narrative',
  'mentor_chat.response': 'Mentor Chat: Response',
  'mentor_chat.tool_planning': 'Mentor Chat: Tool Planning',
  'chat.intent_classification': 'Chat: Intent Classification',
  'development_plan.generate': 'Development Plan',
  'mentor_training.critique': 'Mentor Training: Critique',
  'demo.analysis': 'Demo Analysis',
  'onboarding.intake': 'Onboarding Intake',
  'transcript.postprocess': 'Transcript Postprocess',
  'document.structuring': 'Document Structuring + OCR',
  'school.framework_parse': 'School Framework Parsing',
  'automation.default': 'Automation Default',
};

const deriveDisplayName = (slug: string) =>
  slug
    .split('/', 2)
    .pop()
    ?.replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase()) || slug;

const deriveProvider = (slug: string) => slug.split('/', 1)[0] || 'openrouter';

export default function WorkloadModelRoutingPanel() {
  const [catalog, setCatalog] = useState<ModelCatalogEntry[]>([]);
  const [assignments, setAssignments] = useState<Record<string, WorkloadAssignment>>({});
  const [workloadKeys, setWorkloadKeys] = useState<string[]>([]);
  const [draftSlug, setDraftSlug] = useState('');
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [catalogSaving, setCatalogSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const enabledModels = useMemo(
    () => catalog.filter((model) => model.enabled),
    [catalog],
  );
  // The fallback must be an OpenRouter model (the backend rejects Claude ones).
  const fallbackModels = useMemo(
    () => enabledModels.filter((model) => !isClaudeSlug(model.slug)),
    [enabledModels],
  );

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setMessage(null);
      const [catalogResponse, assignmentsResponse] = await Promise.all([
        apiRequest<ModelCatalogResponse>({ endpoint: '/admin/model-catalog' }),
        apiRequest<WorkloadAssignmentsResponse>({ endpoint: '/admin/model-assignments' }),
      ]);
      setCatalog(catalogResponse.models || []);
      setAssignments(assignmentsResponse.assignments || {});
      setWorkloadKeys(assignmentsResponse.workload_keys || []);
    } catch (error: any) {
      setMessage(error?.message || 'Model routing settings could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const saveCatalog = async (models: ModelCatalogEntry[]) => {
    setCatalogSaving(true);
    setMessage(null);
    try {
      const response = await apiRequest<ModelCatalogResponse, ModelCatalogResponse>({
        endpoint: '/admin/model-catalog',
        method: 'PUT',
        data: { models, version: '0' },
      });
      setCatalog(response.models || []);
      setMessage('Model catalog saved.');
    } catch (error: any) {
      setMessage(error?.message || 'Model catalog could not be saved.');
    } finally {
      setCatalogSaving(false);
    }
  };

  const addModel = async () => {
    const slug = draftSlug.trim();
    if (!slug || !slug.includes('/') || /\s/.test(slug)) {
      setMessage("Use an OpenRouter slug like 'anthropic/claude-haiku-4.5'.");
      return;
    }
    if (catalog.some((model) => model.slug === slug)) {
      setMessage('That model is already in the catalog.');
      return;
    }
    const next = [
      ...catalog,
      {
        slug,
        display_name: deriveDisplayName(slug),
        provider: deriveProvider(slug),
        flags: ['text', 'json'],
        notes: '',
        enabled: true,
      },
    ];
    setDraftSlug('');
    await saveCatalog(next);
  };

  const toggleModel = async (slug: string) => {
    await saveCatalog(
      catalog.map((model) =>
        model.slug === slug ? { ...model, enabled: !model.enabled } : model,
      ),
    );
  };

  const deleteModel = async (slug: string) => {
    await saveCatalog(catalog.filter((model) => model.slug !== slug));
  };

  const updateAssignment = (workloadKey: string, patch: Partial<WorkloadAssignment>) => {
    setAssignments((current) => ({
      ...current,
      [workloadKey]: {
        ...current[workloadKey],
        ...patch,
        workload_key: workloadKey,
      },
    }));
  };

  const putAssignment = async (workloadKey: string, assignment: WorkloadAssignment, doneMessage: string) => {
    setSavingKey(workloadKey);
    setMessage(null);
    try {
      const response = await apiRequest<WorkloadAssignmentsResponse, WorkloadAssignment>({
        endpoint: `/admin/model-assignments/${encodeURIComponent(workloadKey)}`,
        method: 'PUT',
        data: assignmentSavePayload(assignment),
      });
      setAssignments(response.assignments || {});
      setWorkloadKeys(response.workload_keys || []);
      setMessage(doneMessage);
    } catch (error: any) {
      setMessage(error?.message || `Could not save ${workloadKey}.`);
    } finally {
      setSavingKey(null);
    }
  };

  const saveAssignment = async (workloadKey: string) => {
    const assignment = assignments[workloadKey];
    if (!assignment) return;
    await putAssignment(workloadKey, assignment, `${workloadKey} saved.`);
  };

  // One click: run this workload on its OpenRouter fallback model.
  const rollbackToOpenRouter = async (workloadKey: string) => {
    const assignment = assignments[workloadKey];
    const payload = assignment ? rollbackToOpenRouterPayload(assignment) : null;
    if (!payload) return;
    await putAssignment(workloadKey, payload, `${workloadKey} now runs on ${payload.model} (OpenRouter).`);
  };

  if (loading) {
    return (
      <Card>
        <CardContent style={styles.loadingCard}>
          <ActivityIndicator color={ADMIN_COLORS.primary} />
          <Text style={styles.mutedText}>Loading model routing...</Text>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <Text className="text-lg font-semibold text-foreground">Workflow Model Routing</Text>
        <Text className="text-sm text-muted-foreground mt-1">
          Assign a model per workflow. Anthropic API models call Claude directly; every
          workload keeps an OpenRouter fallback that serves it when Claude is unavailable,
          over the monthly cap, or switched off.
        </Text>
      </CardHeader>
      <CardContent style={styles.container}>
        {message ? (
          <View style={styles.messageBox}>
            <Text style={styles.messageText}>{message}</Text>
          </View>
        ) : null}

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Workload assignments</Text>
          {workloadKeys.map((workloadKey) => {
            const assignment = assignments[workloadKey];
            if (!assignment) return null;
            return (
              <View key={workloadKey} style={styles.assignmentRow}>
                <View style={styles.assignmentHeader}>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.workloadTitle}>
                      {WORKLOAD_LABELS[workloadKey] || workloadKey}
                    </Text>
                    <Text style={styles.workloadKey}>{workloadKey}</Text>
                    <Text
                      style={styles.transportBadge}
                      testID={`workload-transport-${workloadKey}`}
                    >
                      {assignmentTransport(assignment) === 'anthropic'
                        ? `Claude API · fallback ${assignment.fallback_model || 'none'}`
                        : 'OpenRouter'}
                    </Text>
                  </View>
                  {assignmentTransport(assignment) === 'anthropic' && assignment.fallback_model ? (
                    <Button
                      variant="outline"
                      disabled={savingKey === workloadKey}
                      onPress={() => rollbackToOpenRouter(workloadKey)}
                      testID={`workload-rollback-${workloadKey}`}
                    >
                      <Text className="text-sm font-semibold">Use OpenRouter</Text>
                    </Button>
                  ) : null}
                  <Button
                    disabled={savingKey === workloadKey}
                    onPress={() => saveAssignment(workloadKey)}
                  >
                    <Text className="text-sm font-semibold text-primary-foreground">
                      {savingKey === workloadKey ? 'Saving...' : 'Save'}
                    </Text>
                  </Button>
                </View>

                <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                  <View style={styles.modelChips}>
                    {enabledModels.map((model) => {
                      const selected = assignment.model === model.slug;
                      return (
                        <TouchableOpacity
                          key={`${workloadKey}-${model.slug}`}
                          onPress={() => updateAssignment(workloadKey, { model: model.slug })}
                          style={[styles.modelChip, selected && styles.modelChipSelected]}
                        >
                          <Text style={[styles.modelChipText, selected && styles.modelChipTextSelected]}>
                            {model.display_name}
                          </Text>
                          <Text style={[styles.modelChipSubtext, selected && styles.modelChipTextSelected]}>
                            {model.slug}
                          </Text>
                        </TouchableOpacity>
                      );
                    })}
                  </View>
                </ScrollView>

                {assignmentTransport(assignment) === 'anthropic' ? (
                  <View style={{ gap: 6 }}>
                    <Text style={styles.fieldLabel}>OpenRouter fallback</Text>
                    <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                      <View style={styles.modelChips}>
                        {fallbackModels.map((model) => {
                          const selected = assignment.fallback_model === model.slug;
                          return (
                            <TouchableOpacity
                              key={`${workloadKey}-fallback-${model.slug}`}
                              onPress={() => updateAssignment(workloadKey, { fallback_model: model.slug })}
                              style={[styles.modelChip, selected && styles.modelChipSelected]}
                            >
                              <Text style={[styles.modelChipText, selected && styles.modelChipTextSelected]}>
                                {model.display_name}
                              </Text>
                              <Text style={[styles.modelChipSubtext, selected && styles.modelChipTextSelected]}>
                                {model.slug}
                              </Text>
                            </TouchableOpacity>
                          );
                        })}
                      </View>
                    </ScrollView>
                  </View>
                ) : null}

                <View style={styles.assignmentFields}>
                  <View style={styles.field}>
                    <Text style={styles.fieldLabel}>Temperature</Text>
                    <TextInput
                      value={assignment.temperature == null ? '' : String(assignment.temperature)}
                      onChangeText={(text) =>
                        updateAssignment(workloadKey, {
                          temperature: text.trim() === '' ? null : Number.parseFloat(text),
                        })
                      }
                      keyboardType="decimal-pad"
                      style={styles.input}
                    />
                  </View>
                  <View style={styles.field}>
                    <Text style={styles.fieldLabel}>Max tokens</Text>
                    <TextInput
                      value={String(assignment.max_tokens)}
                      onChangeText={(text) =>
                        updateAssignment(workloadKey, {
                          max_tokens: Number.parseInt(text, 10) || assignment.max_tokens,
                        })
                      }
                      keyboardType="number-pad"
                      style={styles.input}
                    />
                  </View>
                  <View style={styles.field}>
                    <Text style={styles.fieldLabel}>Reasoning</Text>
                    <View style={styles.reasoningRow}>
                      {REASONING_OPTIONS.map((option) => {
                        const selected = assignment.reasoning_effort === option;
                        return (
                          <TouchableOpacity
                            key={`${workloadKey}-${option || 'none'}`}
                            onPress={() => updateAssignment(workloadKey, { reasoning_effort: option })}
                            style={[styles.reasoningChip, selected && styles.reasoningChipSelected]}
                          >
                            <Text style={[styles.reasoningText, selected && styles.reasoningTextSelected]}>
                              {option || 'none'}
                            </Text>
                          </TouchableOpacity>
                        );
                      })}
                    </View>
                  </View>
                </View>
                <TextInput
                  value={assignment.notes}
                  onChangeText={(text) => updateAssignment(workloadKey, { notes: text })}
                  placeholder="Notes"
                  placeholderTextColor={ADMIN_COLORS.text.muted}
                  style={styles.notesInput}
                />
              </View>
            );
          })}
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Model catalog</Text>
          <View style={styles.addRow}>
            <TextInput
              value={draftSlug}
              onChangeText={setDraftSlug}
              placeholder="openrouter slug, e.g. anthropic/claude-haiku-4.5"
              placeholderTextColor={ADMIN_COLORS.text.muted}
              autoCapitalize="none"
              autoCorrect={false}
              style={[styles.input, { flex: 1 }]}
            />
            <Button disabled={catalogSaving} onPress={addModel}>
              <Text className="text-sm font-semibold text-primary-foreground">Add model</Text>
            </Button>
          </View>

          <View style={styles.catalogList}>
            {catalog.map((model) => (
              <View key={model.slug} style={styles.catalogRow}>
                <View style={{ flex: 1 }}>
                  <Text style={styles.catalogTitle}>{model.display_name}</Text>
                  <Text style={styles.catalogSlug}>{model.slug}</Text>
                  {model.notes ? <Text style={styles.catalogNotes}>{model.notes}</Text> : null}
                </View>
                <View style={styles.catalogActions}>
                  <TouchableOpacity onPress={() => toggleModel(model.slug)} style={styles.actionButton}>
                    <Text style={styles.actionText}>{model.enabled ? 'Disable' : 'Enable'}</Text>
                  </TouchableOpacity>
                  <TouchableOpacity onPress={() => deleteModel(model.slug)} style={styles.deleteButton}>
                    <MaterialIcons name="delete-outline" size={15} color="#991B1B" />
                    <Text style={styles.deleteText}>Delete</Text>
                  </TouchableOpacity>
                </View>
              </View>
            ))}
          </View>
        </View>
      </CardContent>
    </Card>
  );
}

const styles = StyleSheet.create({
  loadingCard: {
    gap: 10,
    alignItems: 'center',
    paddingVertical: 24,
  },
  container: {
    gap: 22,
  },
  section: {
    gap: 12,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: '700',
    color: ADMIN_COLORS.text.primary,
  },
  mutedText: {
    fontSize: 13,
    color: ADMIN_COLORS.text.muted,
  },
  messageBox: {
    borderRadius: 12,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.primaryFaintBorder,
    backgroundColor: ADMIN_COLORS.primaryFaint,
    padding: 12,
  },
  messageText: {
    fontSize: 13,
    color: ADMIN_COLORS.primary,
    fontWeight: '600',
  },
  assignmentRow: {
    gap: 12,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.border,
    backgroundColor: ADMIN_COLORS.background,
    padding: 14,
  },
  assignmentHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  workloadTitle: {
    fontSize: 14,
    fontWeight: '700',
    color: ADMIN_COLORS.text.primary,
  },
  workloadKey: {
    fontSize: 11,
    color: ADMIN_COLORS.text.muted,
    marginTop: 2,
    fontFamily: 'monospace',
  },
  transportBadge: {
    fontSize: 11,
    color: ADMIN_COLORS.primary,
    fontWeight: '700',
    marginTop: 4,
  },
  modelChips: {
    flexDirection: 'row',
    gap: 8,
    paddingRight: 12,
  },
  modelChip: {
    minWidth: 180,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.border,
    backgroundColor: ADMIN_COLORS.surface,
    padding: 10,
  },
  modelChipSelected: {
    borderColor: ADMIN_COLORS.primary,
    backgroundColor: ADMIN_COLORS.primaryFaint,
  },
  modelChipText: {
    fontSize: 12,
    fontWeight: '700',
    color: ADMIN_COLORS.text.primary,
  },
  modelChipSubtext: {
    fontSize: 10,
    color: ADMIN_COLORS.text.muted,
    marginTop: 2,
  },
  modelChipTextSelected: {
    color: ADMIN_COLORS.primary,
  },
  assignmentFields: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  field: {
    minWidth: 150,
    flex: 1,
    gap: 6,
  },
  fieldLabel: {
    fontSize: 11,
    color: ADMIN_COLORS.text.muted,
    fontWeight: '700',
    textTransform: 'uppercase',
  },
  input: {
    borderWidth: 1,
    borderColor: ADMIN_COLORS.border,
    borderRadius: 10,
    paddingHorizontal: 12,
    paddingVertical: 9,
    fontSize: 16, // >=16 stops iOS focus-zoom (FE-17)
    color: ADMIN_COLORS.text.primary,
    backgroundColor: ADMIN_COLORS.surface,
  },
  reasoningRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
  },
  reasoningChip: {
    borderRadius: 999,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.border,
    paddingHorizontal: 9,
    paddingVertical: 6,
    backgroundColor: ADMIN_COLORS.surface,
  },
  reasoningChipSelected: {
    borderColor: ADMIN_COLORS.primary,
    backgroundColor: ADMIN_COLORS.primaryFaint,
  },
  reasoningText: {
    fontSize: 11,
    color: ADMIN_COLORS.text.muted,
    fontWeight: '700',
  },
  reasoningTextSelected: {
    color: ADMIN_COLORS.primary,
  },
  notesInput: {
    borderWidth: 1,
    borderColor: ADMIN_COLORS.border,
    borderRadius: 10,
    paddingHorizontal: 12,
    paddingVertical: 9,
    fontSize: 16, // >=16 stops iOS focus-zoom (FE-17)
    color: ADMIN_COLORS.text.primary,
    backgroundColor: ADMIN_COLORS.surface,
  },
  addRow: {
    flexDirection: 'row',
    gap: 10,
    alignItems: 'center',
  },
  catalogList: {
    borderRadius: 14,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.border,
    overflow: 'hidden',
  },
  catalogRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    padding: 12,
    borderBottomWidth: 1,
    borderBottomColor: ADMIN_COLORS.border,
    backgroundColor: ADMIN_COLORS.surface,
  },
  catalogTitle: {
    fontSize: 13,
    fontWeight: '700',
    color: ADMIN_COLORS.text.primary,
  },
  catalogSlug: {
    fontSize: 11,
    color: ADMIN_COLORS.text.muted,
    fontFamily: 'monospace',
    marginTop: 2,
  },
  catalogNotes: {
    fontSize: 11,
    color: ADMIN_COLORS.text.muted,
    marginTop: 4,
  },
  catalogActions: {
    flexDirection: 'row',
    gap: 8,
    alignItems: 'center',
  },
  actionButton: {
    borderRadius: 999,
    backgroundColor: ADMIN_COLORS.primaryFaint,
    paddingHorizontal: 10,
    paddingVertical: 6,
  },
  actionText: {
    color: ADMIN_COLORS.primary,
    fontSize: 12,
    fontWeight: '700',
  },
  deleteButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    borderRadius: 999,
    backgroundColor: '#FEE2E2',
    paddingHorizontal: 10,
    paddingVertical: 6,
  },
  deleteText: {
    color: '#991B1B',
    fontSize: 12,
    fontWeight: '700',
  },
});
